import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { MDBAccordion, MDBAccordionItem, MDBContainer, MDBCol, MDBRow, MDBInput, MDBIcon, MDBBtn, MDBModal, MDBModalDialog, MDBModalHeader, MDBModalTitle, MDBModalContent, MDBModalBody, MDBModalFooter, MDBBtnGroup } from 'mdb-react-ui-kit';
import KonectValue from './KonectValue';
import { Violation, ViolationImportance, ViolationType } from 'konect-model';
import { AppContext } from '../App';
import "./DesignComposition.css"
import ViolationEntry, { VIOLATION_COLOR } from './ViolationEntry';
import { Settings } from '../Settings';
import { getBacklog } from './GlyphSketching';
import UserControlService from '../../services/user-control-service';
import { nanoid } from 'nanoid';

interface DesignCompositionProps {
}

const DesignComposition = (props: DesignCompositionProps) => {

  const { project } = useContext(AppContext);

  const [violations, setViolations] = useState<Violation[]>([]);

  const [showIgnoredViolations, setShowIgnoredViolations] = useState(Settings.isCollapsedView("ignoredViolations"));

  const [violation, setViolation] = useState<Violation | null>(null);

  const [violationFilter, setViolationFilter] = useState<ViolationType[]>([]);

  const [customViolationMenu, setCustomViolationMenu] = useState(false);

  const [newViolationImportance, setNewViolationImportance] = useState<ViolationImportance>(ViolationImportance.NORMAL);

  useEffect(() => {
    getBacklog(project); //Needed to update glyphs if some changes were made within the ideabox without opening the glyph view
    project.calculateKonectValue();
    setViolations([...project.violations]);
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const onViolationEntryUpdate = useCallback(() => {
    project.calculateKonectValue();
    setViolations([...project.violations]);
  }, [project]);

  const removeViolation = useCallback((v: Violation) => {
    UserControlService.update();
    project.violations = project.violations.filter(vio => vio.id !== v.id);
    onViolationEntryUpdate();
  }, [project, onViolationEntryUpdate]);

  const violationEntries = useMemo(() =>
    project.violations.filter(v => v.importance > 0 && !violationFilter.find(t => t === v.type)).map((violation) =>
      <ViolationEntry key={violation.id} violation={violation} onChange={onViolationEntryUpdate}
        onIgnore={() => { openIgnoreViolationMenu(violation) }} onRemove={() => { removeViolation(violation) }} />)
    , [project, violationFilter, onViolationEntryUpdate, removeViolation]);


  const ignoredViolationEntries = useMemo(() =>
    project.violations.filter(v => v.importance <= 0).map((violation) =>
      <ViolationEntry key={violation.id} violation={violation} onChange={onViolationEntryUpdate}
        onIgnore={() => { openIgnoreViolationMenu(violation) }} onRemove={() => { removeViolation(violation) }} />)
    , [project, onViolationEntryUpdate, removeViolation])

  const toggleIgnored = () => {
    Settings.collapseView("ignoredViolations", !showIgnoredViolations);
    setShowIgnoredViolations(!showIgnoredViolations);
  }

  const ignoreViolation = () => {
    const input = document.getElementById("ignore-reason-input");
    if (!violation || !input) {
      return;
    }
    UserControlService.update();
    violation.importance = ViolationImportance.IGNORE;
    //@ts-ignore
    violation.ignoreReason = input.value;
    onViolationEntryUpdate()
    setViolation(null);
  }

  const openIgnoreViolationMenu = (violation: Violation) => {
    const input = document.getElementById("ignore-reason-input");
    if (!input) {
      return;
    }
    //@ts-ignore
    input.value = violation.ignoreReason
    setViolation(violation);
  }

  const addCustomViolation = () => {
    const nInput = document.getElementById("violation-name-input");
    const rInput = document.getElementById("violation-reason-input");
    if (!nInput || !rInput) return;
    UserControlService.update();
    //@ts-ignore
    const v = new Violation(nanoid(), nInput.value, ViolationType.CUSTOM);
    //@ts-ignore
    v.description = rInput.value;
    v.importance = newViolationImportance;
    project.violations.push(v);
    onViolationEntryUpdate();
    setCustomViolationMenu(false);
  }

  const toggleFilter = (type: ViolationType) => {
    const i = violationFilter.findIndex(t => t === type);
    if (i >= 0) {
      violationFilter.splice(i, 1);
    } else {
      violationFilter.push(type);
    }
    setViolationFilter([...violationFilter]);
  }

  return (
    <>
      <MDBContainer style={{ paddingTop: '20px', paddingBottom: '20px', display: "flex", flexGrow: "1" }}>
        <MDBRow>

          <MDBCol size='md-3'>
            <h3>Design Composition</h3>
            Complex interfaces supporting several parallel tasks and therefore consisting of several glyphs
            need to be carefully designed. Interference between glyphs can have an impact Optimizing Complex
            Graphical Interfaces on the overall operator’s perception accuracy and reaction time. Therefore we
            identiﬁed three guidelines as most promising to have an impact on perception performance and accuracy:
            <p />
            <MDBAccordion alwaysOpen style={{maxHeight: "55vh", overflowY: "auto"}}>
              {/*@ts-ignore*/}
              <MDBAccordionItem collapseId='accordionCollapse1' headerTitle={<>Consistency
              </>}
              >
                <strong>
                  Use the same visual attribute for the same kind of insight for similar important
                  information elements. <p />
                </strong>
                All information elements (i.e. rows) of all idea boxes are sorted according to their insights
                and the chosen visual property for each information is marked (e.g. color or length in the
                efﬁciency ranking column). Then these markings are checked to ensure that the same kind of
                insight (e.g. detect anomaly) is reﬂected by the same visual property (e.g. color).
                Furthermore, the same manifestation of the visual attribute needs to be chosen for the
                same kind of information (e.g. if red is an indicator for a failure, red should be used
                for this kind of information throughout the entire HMI design).
                The consistency is necessary for ensuring that pre-attentive perception of critical
                information is even possible at a global level.
              </MDBAccordionItem>
              <MDBAccordionItem collapseId='accordionCollapse2' headerTitle='Simplicity in Shapes'>
                <strong>
                  Choose simple shapes and visual forms, choose non-accidental
                  visual forms with regard to orientation. <p />
                </strong>
                To ensure simplicity, the metric for estimating the “goodness” of visual shapes must
                be applied. One indicator for visual form simplicity is it’s orientation. If a form is
                perceived as “non-accidental” with regard to orientation (thus it looks the same regardless
                of the viewer’s perspective) it can be understood as a simple form. For calculating the
                form simplicity, all visual shapes on the entire glyph designs are rotated and mirrored.
                Then the number of different appearances of the same form represents an indicator for a
                shapes goodness. The simpler a form is, the easier it can be recognized. More complex
                shapes lead to higher visual clutter causing a decreased recognition performance due to
                “occlusion, greater difﬁculty at both segmenting a scene and performing visual search”.
                Since this metric ﬁgured out to be initially difﬁcult to comprehend, the following figure
                shows an instruction card with an illustrating example.
                <div className="flex" style={{ textAlign: 'center' }}>
                  <img
                    src='./images/InstructionCardShapes.png'
                    width='70%'
                    alt=''
                    loading='lazy'
                  />
                </div>
              </MDBAccordionItem>
              <MDBAccordionItem collapseId='accordionCollapse3' headerTitle='Simplicity in Colors'>
                <strong>
                  Reduce colors for elements that do not carry any information
                  besides from structuring the interface. <p />
                </strong>
                Beside simplicity in shapes also simplicity in colors should be achieved in the user
                interface design. Therefore, each element on the design is systematically looked at
                and it is checked against the table whether a color appearing on the design encodes
                information or if it rather represents visual sugar. If the latter is the case, the color
                should be taken out by being replaced by a neutral color (e.g. grey). This step is
                especially important to maintain the pop-out effects produced by pre-attentive
                visible visual forms on a global level.
              </MDBAccordionItem>
              { /**
              <MDBAccordionItem collapseId='accordionCollapse4' headerTitle='Shortcuts'>
                <strong>
                  Define appropriate keyboard-shortcuts for each task that is not a duplicate.<p />
                </strong>
                Lorem ipsum sum summ summ summ.
              </MDBAccordionItem> */}
            </MDBAccordion> 
          </MDBCol>

          <MDBCol size='md-6 d-flex flex-column'>
            <div className='violation-container border bg-white px-1 flex-fill pb-3' style={{ flexGrow: 0 }}>
              <div className="fst-italic fw-bold mt-3 text-center">
                Design Violations ({violations.filter(v => v.importance > 0).length})
              </div>
              {violations.length > 0 &&
              <div className='d-flex justify-content-center mt-2 align-items-center flex-wrap'>
                <span className="fw-bold me-1 text-center" style={{color: "rgba(0,0,0,0.5)"}}>Filter: </span>
                {Object.values(ViolationType).map((type, index) => violations.find(e => e.type === type && e.importance > 0) ?
                  <span className='violation-type' key={index} style={{ backgroundColor: violationFilter.find(t => t === type) ? "rgba(0,0,0,0.3)" : VIOLATION_COLOR[type], margin: "3px" }}
                    onClick={() => {toggleFilter(type)}}>
                    {type} ({violations.filter(v => v.type === type && v.importance > 0).length})
                  </span> : null
                )}
              </div>
              }
              {violationEntries.length > 0 ?
                violationEntries :
                <div className={"violation-tip"}>There are currently no problems with your HMI</div>
              }
              <MDBBtn style={{ margin: "0 auto" }} onClick={() => { setCustomViolationMenu(true) }}>Add Custom Violation</MDBBtn>
              <div className="fst-italic fw-bold mt-3 text-center">
                <span className="d-inline-flex accept-modal-button" onClick={toggleIgnored} style={{ borderColor: "transparent" }}>
                  {!showIgnoredViolations ?
                    <MDBIcon fas icon='caret-right' style={{ transform: "translateX(25%) scale(1.5)" }} /> :
                    <MDBIcon fas icon='caret-down' style={{ transform: "scale(1.5)" }} />}
                </span> Ignored Violations ({ignoredViolationEntries.length})
              </div>
              {
                showIgnoredViolations ? ignoredViolationEntries.length > 0 ? ignoredViolationEntries :
                  <div className={"violation-tip"}>There are currently no ignored violations</div> : <></>
              }
            </div>
          </MDBCol>


          <MDBCol size='md-3' /*end*/>
            <MDBRow>
              <KonectValue konectValue={project.konectValue} />
            </MDBRow>
          </MDBCol>

        </MDBRow>
      </MDBContainer>

      {<MDBModal staticBackdrop show={!!violation} >
        <MDBModalDialog centered size="md">
          <MDBModalContent>
            <MDBModalHeader>
              <MDBModalTitle>Ignore Violation</MDBModalTitle>
              <MDBBtn className='btn-close' color='none' onClick={() => { setViolation(null) }}></MDBBtn>
            </MDBModalHeader>
            <MDBModalBody>
              <div>Specify a reason why this violation should be ignored.</div><br />
              <MDBInput id='ignore-reason-input' label="Reason" type="text" textarea style={{ minHeight: "20vh" }} />
            </MDBModalBody>
            <MDBModalFooter>
              <MDBBtn onClick={ignoreViolation}>
                Ignore Violation
              </MDBBtn>
              <MDBBtn onClick={() => { setViolation(null) }}>
                Cancel
              </MDBBtn>
            </MDBModalFooter>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>}

      {<MDBModal staticBackdrop show={!!customViolationMenu} >
        <MDBModalDialog centered size="md">
          <MDBModalContent>
            <MDBModalHeader>
              <MDBModalTitle>Add custom violation</MDBModalTitle>
              <MDBBtn className='btn-close' color='none' onClick={() => { setCustomViolationMenu(false) }}></MDBBtn>
            </MDBModalHeader>
            <MDBModalBody>
              <MDBInput id='violation-name-input' label="Title" type="text" wrapperClass='w-50 d-inline-flex' />
              <div style={{ marginLeft: "30px", display: "inline-flex", flexDirection: "column", justifyContent: "space-between" }}>
                <MDBBtnGroup size="sm">
                  <MDBBtn href='#' style={newViolationImportance < 1 && newViolationImportance > 0 ? { backgroundColor: "var(--mdb-dark)", color: "white" } : {}} onClick={() => { setNewViolationImportance(ViolationImportance.LOW) }}>Low</MDBBtn>
                  <MDBBtn href='#' style={newViolationImportance === 1 ? { backgroundColor: "var(--mdb-dark)", color: "white" } : {}} onClick={() => { setNewViolationImportance(ViolationImportance.NORMAL) }}>Normal</MDBBtn>
                  <MDBBtn href='#' style={newViolationImportance > 1 ? { backgroundColor: "var(--mdb-dark)", color: "white" } : {}} onClick={() => { setNewViolationImportance(ViolationImportance.HIGH) }}>High</MDBBtn>
                </MDBBtnGroup>
                <div style={{ fontWeight: "bold", fontSize: "0.75em", marginTop: "0.25rem", color: "gray", textAlign: "center" }}>IMPORTANCE</div>
              </div>
              <br />
              <br />
              <MDBInput id='violation-reason-input' label="Reason" type="text" textarea style={{ minHeight: "20vh" }} />
            </MDBModalBody>
            <MDBModalFooter>
              <MDBBtn onClick={addCustomViolation}>
                Add Violation
              </MDBBtn>
              <MDBBtn onClick={() => { setCustomViolationMenu(false) }}>
                Cancel
              </MDBBtn>
            </MDBModalFooter>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>}
    </>
  );
}

export default DesignComposition;